import { useEffect, useRef, useState } from 'react';

/**
 * Registers the service worker and offers a reload when a new version has been downloaded. The new version
 * waits until the user chooses to reload, so an update never interrupts a form being filled in.
 */
export function UpdatePrompt() {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  // The first install also claims the page (a controller change), which must not reload it.
  const accepted = useRef(false);

  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
    const onControllerChange = () => { if (accepted.current) window.location.reload(); };
    const onVisible = () => { if (document.visibilityState === 'visible') navigator.serviceWorker.getRegistration().then((registration) => registration?.update()).catch(() => undefined); };
    navigator.serviceWorker.register('/sw.js').then((registration) => {
      const track = (worker: ServiceWorker | null) => {
        worker?.addEventListener('statechange', () => { if (worker.state === 'installed' && navigator.serviceWorker.controller) setWaiting(worker); });
      };
      if (registration.waiting && navigator.serviceWorker.controller) setWaiting(registration.waiting);
      registration.addEventListener('updatefound', () => track(registration.installing));
    }).catch((error) => console.warn('Service worker registration failed', error));
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    // Check for a new deploy when the app comes back to the foreground.
    document.addEventListener('visibilitychange', onVisible);
    return () => { navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  if (!waiting) return null;
  return (
    <div className="update-prompt" role="status">
      <span>A new version of PCS Internal is ready.</span>
      <button className="btn btn-primary btn-sm" onClick={() => { accepted.current = true; waiting.postMessage({ type: 'pcs-activate-update' }); }}>Reload</button>
      <button className="btn-link" onClick={() => setWaiting(null)}>Later</button>
    </div>
  );
}
